import { format } from 'date-fns';
import { useMutation } from '@tanstack/react-query';

import { putJob, deleteJobLabour, postJobLabour } from 'queries';
import { Condition, JsonData, JsonDataItem } from 'types';
import { parseErrors, Toast } from 'helpers';

export const useServiceProductsUpdate = () => {
  const handleMutationError = (error: JsonDataItem) => {
    Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
  };

  const {
    mutate: updateLegacyJob,
    isLoading: isLegacyUpdating,
    isError: isJobError,
  } = useMutation(putJob, {
    onError: handleMutationError,
  });
  const {
    mutate: addLegacyLabourRate,
    isLoading: isLegacyRateAdding,
    isError: isLabourRateError,
  } = useMutation(postJobLabour, {
    onError: handleMutationError,
  });
  const {
    mutate: deleteLegacyLabourRate,
    isLoading: isLegacyRateDeleting,
    isError: isDeleteError,
  } = useMutation(deleteJobLabour, {
    onError: handleMutationError,
  });

  const isLoading = isLegacyUpdating || isLegacyRateAdding || isLegacyRateDeleting;

  const isError = isJobError || isLabourRateError || isDeleteError;

  // TODO: remove legacy job update logic when support is over
  const updateLegacyProducts = (props: JsonDataItem) => {
    const { serviceId, initialData, newData } = props;

    // check for removed consultations
    const findRemovedConsultations = (initialConsultations: JsonData, newServices: JsonData) => {
      const initialIds = initialConsultations.map((consultation) => consultation.ID);
      const newConsultationIds = newServices
        .filter((service) => service.id.startsWith('existing_consultation_'))
        .map((service) => parseInt(service.id.replace('existing_consultation_', ''), 10));

      return initialIds.filter((id) => !newConsultationIds.includes(id));
    };
    const removedIds = findRemovedConsultations(initialData.consultations, newData);
    removedIds.forEach((labourId) => deleteLegacyLabourRate({ serviceId, labourId }));

    // check for added consultations
    const findNewConsultations = (initialConsultations: JsonData, newServices: JsonData) => {
      const initialIds = initialConsultations.map((consultation) => consultation.ID);
      const newConsultations = newServices
        .filter((service) => service.id.startsWith('new_consultation_'))
        .filter((service) => {
          const newConsultationId = parseInt(service.id.replace('new_consultation_', ''), 10);
          return !initialIds.includes(newConsultationId);
        });

      return newConsultations.map((service) => ({
        date: service.date || '',
        hours: service.qty,
      }));
    };
    const newConsultations = findNewConsultations(initialData.consultations, newData);
    newConsultations.forEach((consultation) =>
      addLegacyLabourRate({
        serviceId,
        date: format(new Date(consultation.date), 'yyyy-MM-dd'),
        hours: consultation.hours,
      }),
    );

    // check for legacy flags change (sds, weekend)
    const findServiceFlagChanges = (initialData: JsonDataItem, newServices: JsonData): JsonDataItem | null => {
      const sameDayService =
        newServices.find((service) => service.id === 'sds')?.qty === 1 ? Condition.Yes : Condition.No;
      const weekendService =
        newServices.find((service) => service.id === 'weekend')?.qty === 1 ? Condition.Yes : Condition.No;
      if (sameDayService !== initialData.job.sameday_service || weekendService !== initialData.job.weekend_service) {
        return { sameDayService: sameDayService, weekendService: weekendService };
      }
      return null;
    };
    const serviceFlagChanges = findServiceFlagChanges(initialData, newData);
    serviceFlagChanges && updateLegacyJob({ serviceId, ...serviceFlagChanges });
  };

  return { updateLegacyProducts, isLoading, isError };
};
